/**
 * Modern Form Component
 * Reusable form components with modern design
 */

import React from 'react';
import './ModernForm.css';

// Form Field Component
export const FormField = ({ 
  label, 
  error, 
  hint, 
  required = false, 
  children, 
  className = '' 
}) => {
  return (
    <div className={`form-field ${className}`}>
      {label && (
        <label className="form-field__label">
          {label}
          {required && <span className="form-field__required">*</span>}
        </label>
      )}
      <div className="form-field__input">
        {children}
      </div>
      {hint && !error && (
        <p className="form-field__hint">{hint}</p>
      )}
      {error && (
        <p className="form-field__error">{error}</p>
      )}
    </div>
  );
};

// Input Component
export const ModernInput = ({ 
  type = 'text',
  placeholder,
  value,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  error = false,
  className = '',
  ...props 
}) => {
  const inputClasses = [
    'modern-input',
    error ? 'modern-input--error' : '',
    disabled ? 'modern-input--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <input
      type={type}
      className={inputClasses}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      disabled={disabled}
      {...props}
    />
  );
};

// Textarea Component
export const ModernTextarea = ({ 
  placeholder,
  value,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  error = false,
  rows = 4,
  className = '',
  ...props 
}) => {
  const textareaClasses = [
    'modern-textarea',
    error ? 'modern-textarea--error' : '',
    disabled ? 'modern-textarea--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <textarea
      className={textareaClasses}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      disabled={disabled}
      rows={rows}
      {...props}
    />
  );
};

// Select Component
export const ModernSelect = ({ 
  children,
  value,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  error = false,
  className = '',
  ...props 
}) => {
  const selectClasses = [
    'modern-select',
    error ? 'modern-select--error' : '',
    disabled ? 'modern-select--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <select
      className={selectClasses}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      disabled={disabled}
      {...props}
    >
      {children}
    </select>
  );
};

// Checkbox Component
export const ModernCheckbox = ({ 
  label,
  checked,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  error = false,
  className = '',
  ...props 
}) => {
  const checkboxClasses = [
    'modern-checkbox',
    error ? 'modern-checkbox--error' : '',
    disabled ? 'modern-checkbox--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <label className={checkboxClasses}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={disabled}
        {...props}
      />
      <span className="modern-checkbox__checkmark"></span>
      {label && <span className="modern-checkbox__label">{label}</span>}
    </label>
  );
};

// Radio Component
export const ModernRadio = ({ 
  label,
  name,
  value,
  checked,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  error = false,
  className = '',
  ...props 
}) => {
  const radioClasses = [
    'modern-radio',
    error ? 'modern-radio--error' : '',
    disabled ? 'modern-radio--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <label className={radioClasses}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={disabled}
        {...props}
      />
      <span className="modern-radio__checkmark"></span>
      {label && <span className="modern-radio__label">{label}</span>}
    </label>
  );
};

// Button Component
export const ModernButton = ({ 
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon,
  iconPosition = 'left',
  onClick,
  type = 'button',
  className = '',
  ...props 
}) => {
  const buttonClasses = [
    'modern-button',
    `modern-button--${variant}`,
    `modern-button--${size}`,
    disabled ? 'modern-button--disabled' : '',
    loading ? 'modern-button--loading' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <span className="modern-button__spinner"></span>
      )}
      {icon && iconPosition === 'left' && !loading && (
        <span className="modern-button__icon modern-button__icon--left">{icon}</span>
      )}
      <span className="modern-button__content">{children}</span>
      {icon && iconPosition === 'right' && !loading && (
        <span className="modern-button__icon modern-button__icon--right">{icon}</span>
      )}
    </button>
  );
};

// Form Component
export const ModernForm = ({ 
  children,
  onSubmit,
  className = '',
  ...props 
}) => {
  return (
    <form
      className={`modern-form ${className}`}
      onSubmit={onSubmit}
      {...props}
    >
      {children}
    </form>
  );
};

// Form Group Component
export const FormGroup = ({ 
  children,
  className = '',
  ...props 
}) => {
  return (
    <div className={`form-group ${className}`} {...props}>
      {children}
    </div>
  );
};

// Form Row Component
export const FormRow = ({ 
  children,
  className = '',
  ...props 
}) => {
  return (
    <div className={`form-row ${className}`} {...props}>
      {children}
    </div>
  );
};
